"use strict";

const _ = require("lodash");

class GridCellRenderer {
    constructor(model) {
        this._model = model;
        this._rootElem = document.createElement("grid-cell");
        this._rootElem.component = this._model;

        let colRendererComponent = this._model.getColumnRendererComponent();

        if (this._model.getEntityColumn() === true) {
            if (_.isUndefined(this._model.getContext())) {
                this._cellRendererComponent = new colRendererComponent(this._model.getEntity());
            }
            else {
                this._cellRendererComponent = new colRendererComponent(this._model.getEntity(), this._model.getContext());
            }
        } else {
            if (_.isUndefined(this._model.getContext())) {
                this._cellRendererComponent = new colRendererComponent(this._model.getValue());
            }
            else {
                this._cellRendererComponent = new colRendererComponent(this._model.getValue(), this._model.getContext());
            }
        }

        this._registerListeners();
        this.render();
    }
    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/
    _shutdown() {
        let promiseArray = [];
        this._unregisterListeners();
        this._model = undefined;
        this._rootElem.remove();
        this._rootElem = undefined;
        let cellShutdownPromise = this._cellRendererComponent.shutdown().then(() => {
            this._cellRendererComponent = undefined;
        });
        promiseArray.push(cellShutdownPromise);

        return Promise.all(promiseArray);
    }
    _registerListeners() {
        this._model.on("ID", (this._onId = this._onId.bind(this)));
        this._model.on("Value", (this._onValue = this._onValue.bind(this)));
        this._model.on("Width", (this._onWidth = this._onWidth.bind(this)));
        this._model.on("Shown", (this._onShown = this._onShown.bind(this)));
        this._cellRendererComponent.on(
            "UserCellAction",
            (this._onUserCellAction = this._onUserCellAction.bind(this))
        );
    }
    _unregisterListeners() {
        this._model.removeAllListeners("ID");
        this._model.removeAllListeners("Value");
        this._model.removeAllListeners("Width");
        this._model.removeAllListeners("Shown");
        this._cellRendererComponent.removeAllListeners("UserCellAction");
    }
    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/
    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering. If a non
     * Promise is returned the caller will assume the Renderer has completed rendering.
     */
    render() {
        let prom = new Promise((resolve /*,reject*/) => {
            this._rootElem.innerHTML = "";
            this._rootElem.appendChild(this._cellRendererComponent.renderer.rootElement);
            this._setId(this._model.getId());
            this._setWidth(this._model.getWidth());
            this._setShown(this._model.getShown());
            resolve();
        });
        return prom;
    }
    get rootElement() {
        return this.getRootElement();
    }
    getRootElement() {
        return this._rootElem;
    }
    get model() {
        return this.getModel();
    }
    getModel() {
        return this._model;
    }
    /*******************************************/
    /* END COMPONENT RENDERER METHODS */
    /* START HANDLER METHODS */
    /*******************************************/

    /**
     * Fired when the id has been updated in the model
     * @param newVal {string}
     * @private
     */
    _onId(newVal) {
        this._setId(newVal);
    }

    /**
     * Fired when the value has been updated in the model
     * @param newVal {boolean|object|number|array|string}
     * @private
     */
    _onValue(newVal) {
        this._setValue(newVal);

        // Change frozen-rows when value changes
        const gridComponent = this._model._column._grid;
        const gridRenderer = gridComponent.getRenderer();
        const entityId = _.get(this._model._entity, "id", "");
        if (!_.isEmpty(entityId)) {
            const isFrozenRow = gridRenderer._rowComponents[entityId]
                .getRenderer()
                .getRootElement()
                .classList.contains("frozen-row");
            if (isFrozenRow && gridComponent.rowsClickable(this._model._entity)) {
                gridRenderer._unfreezeRow(entityId);
            }
            else if (!isFrozenRow && !gridComponent.rowsClickable(this._model._entity)) {
                gridRenderer._freezeRow(entityId);
            }
        }
    }

    /**
     * Fired when the width has been updated in the model
     * @param newVal ()
     * @private
     */
    _onWidth(newVal) {
        this._setWidth(newVal);
    }

    /**
     * Fired when the shown state been updated in the model
     * @param newVal {Boolean} - The shown state
     * @private
     */
    _onShown(newVal) {
        this._setShown(newVal);
    }

    /**
     * Firec when the user has selected an action inside of the cell
     * @param eventData
     * @private
     */
    _onUserCellAction(eventData) {
        this._model._triggerCellAction(eventData);
    }

    /*******************************************/
    /* END HANDLER METHODS */
    /* START PRIVATE API METHODS */
    /*******************************************/

    /**
     * Set the id as a class in the DOM
     * @param newVal {string}
     * @private
     */
    _setId(newVal) {
        if (!_.isUndefined(newVal)) {
            this._rootElem.classList.add(newVal);
        }
    }

    /**
     * Set the width
     * @param newVal {number}
     * @private
     */
    _setWidth(newVal) {
        if (!_.isUndefined(newVal)) {
            if (typeof newVal === "number") {
                this._rootElem.style.width = newVal / 10 + "rem";
            } else {
                console.error(
                    "Specify column width as a Number representing the desired pixel size. It will be converted to REM units."
                );
            }
        }
    }

    /**
     * Set the shown state
     * @param newVal {Boolean} - The shown state
     * @private
     */
    _setShown(newVal) {
        if (newVal === false) {
            this._rootElem.classList.add("hidden");
        } else {
            this._rootElem.classList.remove("hidden");
        }
    }

    /**
     * Pass the new value along to the cell renderer component
     * @param newVal {boolean}
     * @private
     */
    _setValue(newVal) {
        this._cellRendererComponent.setValue(newVal);
    }

    /*******************************************/
    /*  END PRIVATE API METHODS */
    /*******************************************/
}

module.exports = GridCellRenderer;
